import { zodResolver } from "@hookform/resolvers/zod"
import { formatISO } from "date-fns"
import type { LucideIcon } from "lucide-react"
import {
  ArrowUpCircleIcon,
  SearchIcon,
  Trash2Icon,
  XCircleIcon,
} from "lucide-react"
import { useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import {
  useNavigate,
  useSearchParams,
} from "react-router-dom"

import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { FilterDefaultersStatusEnum } from "@/enums"
import { formatDocument } from "@/helpers/format"
import { useDefaulterSearchParams } from "@/hooks"
import { toast } from "@/hooks/use-toast"
import { cn } from "@/lib/utils"
import type { DefaultersFilterFormSchemaProps } from "@/schemas/filter-defaulters"
import { defaultersFilterFormSchema } from "@/schemas/filter-defaulters"
import { DateHelper } from "@/utils"

import { AppDateInput } from "../commons"
const statusOptionsOrder = [
  {
    value: FilterDefaultersStatusEnum.EXPIRED,
    label: "Pedido expirado",
    icon: XCircleIcon,
  },
  {
    value: FilterDefaultersStatusEnum.PENDING,
    label: "Pedido que não foi pago",
    icon: ArrowUpCircleIcon,
  },
  {
    value: FilterDefaultersStatusEnum.CANCELED,
    label: "Pedido rejeitado",
    icon: XCircleIcon,
  },
]
type StatusOption = {
  value: FilterDefaultersStatusEnum
  label: string
  icon: LucideIcon
}
export const DefaultersRotateFilterForm = () => {
  const [searchParams] = useSearchParams()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { params, startDateTime, endDateTime } =
    useDefaulterSearchParams()
  const [statusSelected, setStatusSelected] = useState<
    StatusOption | undefined
  >(
    statusOptionsOrder.find(
      (status) => status.value === params?.queryType
    )
  )
  const schema = useMemo(
    () => defaultersFilterFormSchema(t),
    [t]
  )
  const form = useForm<DefaultersFilterFormSchemaProps>({
    resolver: zodResolver(schema),
    defaultValues: {
      queryType: FilterDefaultersStatusEnum.EXPIRED,
      startDateTime: new Date(
        DateHelper.formatToISO(params.startDateTime)
      ),
      endDateTime: new Date(
        DateHelper.formatToISO(params.endDateTime)
      ),
      name: params?.name,
      document: params?.document,
      ownId: params?.ownId,
      code: params?.code,
    },
  })
  const hasFilter = useMemo(() => {
    return (
      !!params?.code ||
      !!params?.document ||
      !!params?.name ||
      !!params?.ownId ||
      !!params?.queryType
    )
  }, [params])
  const handleSubmit = form.handleSubmit((data) => {
    if (data.startDateTime > data.endDateTime) {
      toast({
        title:
          "Data de início não pode ser maior que a data final.",
      })
      return
    }
    const params = new URLSearchParams()
    const pageSize = searchParams.get("pageSize")
    if (pageSize) params.set("pageSize", pageSize)
    Object.entries(data).forEach(([key, value]) => {
      if (value) {
        if (value === "ALL") return
        if (value instanceof Date) {
          value = formatISO(value)
        }
        params.set(key, value)
      }
      params.set("tab", "rotate")
      params.set("refetch", "true")
    })
    navigate(`?${params.toString()}`)
  })
  const handleResetForm = () => {
    form.reset({
      name: "",
      document: "",
      ownId: "",
      code: "",
      queryType: "ALL",
      startDateTime,
      endDateTime,
    })
    setStatusSelected(statusOptionsOrder[0])
    const params = new URLSearchParams()
    Object.entries({ params }).forEach(([key]) => {
      if (params.get(key)) {
        params.delete(key)
      }
      params.set("refetch", "true")
      params.set("tab", "rotate")
    })
    navigate(`?${params.toString()}`)
  }
  return (
    <div className="@container">
      <Form {...form}>
        <form
          onSubmit={handleSubmit}
          className="grid gap-4 @4xl:grid-cols-[1fr_auto]"
        >
          <div className="grid gap-4 @xl:grid-cols-2 @3xl:grid-cols-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input placeholder="Nome" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="document"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      placeholder="Buscar por CPF / CNPJ"
                      maxLength={18}
                      {...field}
                      onChange={(e) => {
                        const masked = formatDocument(
                          e.target.value
                        )
                        field.onChange(masked)
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="ownId"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      placeholder="Buscar por ID do Cliente"
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormControl>
                    <Input
                      placeholder="Buscar por código"
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="queryType"
              defaultValue={params?.queryType}
              render={({ field }) => (
                <FormItem className="w-full">
                  <Popover>
                    <FormControl>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="h-10 w-full justify-start truncate"
                        >
                          {field.value && statusSelected ? (
                            <>
                              <statusSelected.icon className="mr-2 h-4 w-4 shrink-0" />
                              <span className="max-w-[90%] truncate">
                                {statusSelected?.label}
                              </span>
                            </>
                          ) : (
                            <>+ Selecione um status</>
                          )}
                        </Button>
                      </PopoverTrigger>
                    </FormControl>
                    <PopoverContent
                      className="p-0"
                      align="end"
                    >
                      <Command>
                        <CommandInput placeholder="Procurar status" />
                        <CommandList>
                          <CommandEmpty>
                            Nenhum status encontrado.
                          </CommandEmpty>
                          <CommandGroup>
                            {statusOptionsOrder.map(
                              (status) => (
                                <CommandItem
                                  key={status.label}
                                  value={status.label}
                                  onSelect={() => {
                                    form.setValue(
                                      "queryType",
                                      status.value
                                    )
                                    setStatusSelected(
                                      status
                                    )
                                  }}
                                >
                                  <status.icon
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      status.value ===
                                        field?.value
                                        ? "opacity-100"
                                        : "opacity-40"
                                    )}
                                  />
                                  <span>
                                    {status.label}
                                  </span>
                                </CommandItem>
                              )
                            )}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="startDateTime"
              render={({ field }) => (
                <FormItem className="w-full shrink-0">
                  <FormControl>
                    <AppDateInput
                      onSelect={field.onChange}
                      selected={field.value}
                      placeholder="Data inicial"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="endDateTime"
              render={({ field }) => (
                <FormItem className="w-full shrink-0">
                  <FormControl>
                    <AppDateInput
                      onSelect={field.onChange}
                      selected={field.value}
                      placeholder="Data final"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <div className="flex w-full flex-col justify-start gap-4 @xl:flex-row-reverse @4xl:flex-col">
            <Button
              type="submit"
              className="btn-whitelabel"
            >
              <SearchIcon size={18} className="mr-2" />
              Buscar
            </Button>
            {hasFilter && (
              <Button
                type="button"
                variant="outline"
                onClick={handleResetForm}
              >
                <Trash2Icon size={18} className="mr-2" />
                Limpar
              </Button>
            )}
          </div>
        </form>
      </Form>
    </div>
  )
}
